import React, { useState } from "react";
import { FaChevronDown, FaTrash, FaEdit } from "react-icons/fa";
import styles from "../../styles/patient-page-modules/PatientSettings.module.css";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import DatePicker from "../shared-modules/DatePicker/DatePicker";
import {
  getCaseManagers,
  getLanguages,
} from "../../services/case-manager/case-manager";
import {
  getPatientContacts,
  deletePatientContact,
} from "../../services/patient/settings";
import { getOrganizationDetails } from "../../services/admin/organization";
import Input from "../ui/input";

import Spinner from "../spinner";
import Reload from "../ui/reload-data";
import Button from "../button-v1";
import AddContacts from "./add-contacts";
import { toast } from "react-hot-toast";
import AlertDialogue from "../alert-dialogue";
import { getUserRoleSlug } from "../../utils/authorization";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
const PatientInfo = ({
  formData,
  handleChange,
  handleCheckBoxChange,
  isOpen,
  toggleSection,
  formError,
  setFormError,
  handleBlur,
}) => {
  const patientId = sessionStorage.getItem("_pt_id");
  const locationId = sessionStorage.getItem("_loc_id");
  const [open, setOpen] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [contactId, setContactId] = useState(null);
  const queryClient = useQueryClient();
  const roleSlug = getUserRoleSlug();
  const showVitalSmsToggle = ["toca_admin", "toca_care_manager", "toca_super_admin"].includes(
    roleSlug
  );
 
  const {
    data: caseManagers,
    isLoading: isLoadingCaseManagers,
    error: caseManagerError,
    refetch: refetchCaseManagers,
  } = useQuery(["caseManagers"], () => getCaseManagers(true));

  const {
    data: languages,
    isLoading: isLoadingLanguages,
    error: languagesError,
    refetch: refetchLanguages,
  } = useQuery(["languages"], () => getLanguages());

  const {
    data: patientContacts,
    isLoading: isLoadingPatientContacts,
    error: patientContactsError,
    refetch: refetchPatientContacts,
  } = useQuery(["patientContacts", patientId], () =>
    getPatientContacts(patientId, locationId)
  );

  const { data: organizationDetails } = useQuery(["organization-details"], () =>
    getOrganizationDetails()
  );

  const {
    mutate: deleteContactMutation,
    isLoading: isDeletingContact,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
    error: deleteError,
  } = useMutation(
    (contactId) => deletePatientContact(contactId, patientId, locationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "patientContacts",
          "patient-general-details",
        ]);
        toast.success("Contact deleted successfully");
        queryClient.invalidateQueries(["patientContacts"]);
        setDeleteAlertOpen(false);
      },
      onError: (error) => {
        toast.error(error.response.data.detail || "No Contact Found");
      },
    }
  );

  const handleDeleteContact = (contactId) => {
    setContactToDelete(contactId);
    setDeleteAlertOpen(true);
  };

  const handleEditContact = (contactId) => {
    setContactId(contactId);
    setOpen(true);
  };

  // Intentionally avoid custom conversion helpers per requirement

  if (caseManagerError)
    return (
      <Reload message="Could not load data" onReload={refetchCaseManagers} />
    );

  if (languagesError)
    return <Reload message="Could not load data" onReload={refetchLanguages} />;

  return (
    <div className={`${styles.form_section} ${isOpen ? "" : styles.close}`}>
      <div
        className={styles.section_header}
        onClick={() => toggleSection("sec1")}
      >
        <div className={styles.section_number}>1</div>
        <div>
          <p className={styles.section_heading}>Patient</p>
          <p className={styles.sub_heading}>Basic Patient Information</p>
        </div>
        <div className={styles.drop_icon}>
          <FaChevronDown />
        </div>
      </div>
      {isLoadingCaseManagers ||
        isLoadingLanguages ||
        isLoadingPatientContacts ? (
        <Spinner />
      ) : (
        <div className={styles.input_fields}>
          <div className={styles.input_group}>
            {/* First Name */}
            <div className={styles.input_wrapper}>
              <Input
                id="first_name"
                type="text"
                placeholder="First Name"
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                error={formError?.first_name}
                onBlur={handleBlur}
              />
            </div>

            {/* Last Name */}
            <div className={styles.input_wrapper}>
              <Input
                id="last_name"
                type="text"
                placeholder="Last Name"
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                error={formError?.last_name}
                onBlur={handleBlur}
              />
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Username */}
            <div className={styles.input_wrapper}>
              <label>Username</label>
              <input
                type="text"
                placeholder="Username"
                name="username"
                autoComplete="off"
                className={styles.input}
                value={formData.username}
                onChange={handleChange}
              />
            </div>

            {/* Email */}
            <div className={styles.input_wrapper}>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                label="Email"
                name="email"
                autoComplete="off"
                value={formData.email}
                onChange={handleChange}
                required
                error={formError?.email}
                onBlur={handleBlur}
              />
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Password */}
            <div className={styles.input_wrapper}>
              {/* <label>Password</label> */}
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                label="Password"
                name="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                // variant="filled"
                // size="medium"
                error={formError?.password}
                // startIcon={<LockIcon />}
                onBlur={handleBlur}
                helperText="Must be at least 8 characters long"
              />
            </div>

            {/* Gender */}
            <div className={styles.input_wrapper}>
              <label>Gender</label>
              <Select
                onChange={(value) =>
                  handleChange({
                    target: {
                      name: "gender",
                      value: value,
                    },
                  })
                }
                value={formData.gender}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select gender">
                    {formData.gender ?? "Select gender"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Confirm Password */}
            <div className={styles.input_wrapper}>
              {/* <label>Confirm password</label> */}
              <Input
                id="confirm_password"
                type="password"
                placeholder="Enter your password"
                label="Confirm Password"
                name="confirm_password"
                autoComplete="new-password"
                value={formData.confirm_password}
                onChange={handleChange}
                // variant="filled"
                // size="medium"
                error={formError?.confirm_password}
              // startIcon={<LockIcon />}

              // helperText="Must be at least 8 characters long"
              />
            </div>

            {/* Date of Birth */}
            <div className={styles.input_wrapper}>
              <label>Date of Birth</label>
              <DatePicker
                selectedDate={formData.dob}
                autoFormatDate={true}
                onCancelSelect={() =>
                  handleChange({
                    target: {
                      name: "dob",
                      value: "",
                    },
                  })
                }
                onDateSelect={(selectedDate) => {
                  handleChange({
                    target: {
                      name: "dob",
                      value: selectedDate,
                    },
                  });
                }}
                placeholder="MM-DD-YYYY"
              />
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Case Manager */}
            <div className={styles.input_wrapper}>
              <label>Case Manager</label>
              <Select
                onChange={(value) =>
                  handleChange({
                    target: {
                      name: "case_manager",
                      value: value,
                    },
                  })
                }
                value={formData.case_manager}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select case manager">
                    {(() => {
                      const cm = caseManagers?.find(
                        (_cm) => _cm.id === formData.case_manager
                      );
                      return cm
                        ? cm.name
                        : "Select case manager";
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent searchField={true}>
                  {caseManagers?.map((caseManager) => (
                    <SelectItem
                      key={caseManager.id}
                      value={caseManager.id}
                      disabled={
                        !caseManager.can_be_assigned &&
                        caseManager.id !== formData.case_manager  // 👈 if already assigned to this patient, don't disable
                      }
                    >
                      {caseManager.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Primary Phone */}
            <div className={styles.input_wrapper}>
              <Input
                id="primary_phone"
                type="text"
                placeholder="Enter your number"
                label="Primary Phone"
                name="primary_phone"
                value={formData.primary_phone}
                onChange={handleChange}
                mask={true}
                maskPattern="(999) 999-9999"
                error={formError?.primary_phone}
              // startIcon={<LockIcon />}

              // helperText="Must be at least 8 characters long"
              />
            </div>
          </div>

          <div className={styles.input_group}>
            <div className={styles.input_wrapper}>
              <label>Preferred Language</label>
              <Select
                onChange={(value) =>
                  handleChange({
                    target: {
                      name: "preferred_language",
                      value: value,
                    },
                  })
                }
                value={formData.preferred_language}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select preferred language">
                    {formData.preferred_language ?? "Select preferred language"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {languages?.map((language) => (
                    <SelectItem key={language.id} value={language.name}>
                      {language.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className={styles.input_wrapper}>
              <label>Consent Date</label>
              <DatePicker
                selectedDate={formData.created_at}
                onCancelSelect={() => {
                  handleChange({
                    target: {
                      name: "created_at",
                      value: "",
                    },
                  });
                }}
                onDateSelect={(selectedDate) => {
                  handleChange({
                    target: {
                      name: "created_at",
                      value: selectedDate,
                    },
                  });
                }}
                placeholder="Select consent date"
              />
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Medical Record Number */}
            <div className={styles.input_wrapper}>
              <label>Medical Record Number</label>
              <input
                type="text"
                placeholder="Enter MRN"
                name="medical_record_number"
                className={styles.input}
                value={formData.medical_record_number}
                onChange={handleChange}
              />
            </div>

            <div className={styles.input_wrapper}></div>
          </div>

          {/* Programs and Contacts Row */}
          <div className={styles.input_group}>
            {/* Programs Wrapper */}
            <div className={styles.input_wrapper}>
              <label>Programs:</label>
              <div
                className={styles.program_container}
                style={{
                  display: "grid",
                  gridTemplateColumns: "100px 1fr",
                  gap: "1rem",
                  marginTop: "0.5rem",
                  alignItems: "center",
                }}
              >
                {/* RPM Row */}
                <div className={styles.switch_wrapper} style={{ margin: 0 }}>
                  <label className={`${styles.switch}`}>
                    <input
                      type="checkbox"
                      name="rpm"
                      className={`${styles.input} ${styles.checkbox}`}
                      checked={formData.rpm}
                      onChange={handleCheckBoxChange}
                      disabled={!formData.ccm}
                    />
                    <span
                      className={styles.slider}
                      style={
                        !formData.ccm
                          ? { cursor: "not-allowed", opacity: "0.5" }
                          : undefined
                      }
                    ></span>
                  </label>
                  <span>RPM</span>
                </div>

                <div
                  style={{
                    minHeight: "40px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {formData.rpm && (
                    <div style={{ width: "100%", maxWidth: "220px" }}>
                      <DatePicker
                        selectedDate={formData.rpm_enrolled_at}
                        onCancelSelect={() => {
                          handleChange({
                            target: {
                              name: "rpm_enrolled_at",
                              value: "",
                            },
                          });
                        }}
                        onDateSelect={(selectedDate) => {
                          handleChange({
                            target: {
                              name: "rpm_enrolled_at",
                              value: selectedDate,
                            },
                          });
                        }}
                        placeholder="RPM Enrollment Date"
                      />
                    </div>
                  )}
                </div>

                {/* CCM Row */}
                <div className={styles.switch_wrapper} style={{ margin: 0 }}>
                  <label className={`${styles.switch}`}>
                    <input
                      type="checkbox"
                      name="ccm"
                      className={`${styles.input} ${styles.checkbox}`}
                      checked={formData.ccm}
                      onChange={handleCheckBoxChange}
                    />
                    <span className={styles.slider}></span>
                  </label>
                  <span>CCM</span>
                </div>

                <div
                  style={{
                    minHeight: "40px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {formData.ccm && (
                    <div style={{ width: "100%", maxWidth: "220px" }}>
                      <DatePicker
                        selectedDate={formData.ccm_enrolled_at}
                        onCancelSelect={() => {
                          handleChange({
                            target: {
                              name: "ccm_enrolled_at",
                              value: "",
                            },
                          });
                        }}
                        onDateSelect={(selectedDate) => {
                          handleChange({
                            target: {
                              name: "ccm_enrolled_at",
                              value: selectedDate,
                            },
                          });
                        }}
                        placeholder="CCM Enrollment Date"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contacts Wrapper */}
            <div className={styles.input_wrapper}>
              <label>Contacts</label>
              {patientContacts &&
                patientContacts?.map((contact) => (
                  <section className={styles.contact_wrapper} key={contact.id}>
                    <input
                      className="input_field"
                      key={contact.id}
                      mask={true}
                      maskPattern="(999) 999-9999"
                      type="text"
                      value={contact.number}
                      readOnly
                    />

                    <Button
                      variant="normal"
                      onClick={() => handleEditContact(contact.id)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="normal"
                      onClick={() => handleDeleteContact(contact.id)}
                    >
                      <FaTrash />
                    </Button>
                  </section>
                ))}
              <AddContacts
                open={open}
                setOpen={setOpen}
                contactId={contactId}
              />
              <Button
                onClick={() => {
                  setOpen(true);
                  setContactId(null);
                }}
                disabled={patientContacts?.length >= 2 ? true : false}
              >
                Add Contact
              </Button>
              {showVitalSmsToggle && organizationDetails?.vital_sms_alert && (
                <div
                  className={styles.switch_wrapper}
                  style={{ marginTop: "1rem" }}
                >
                  <label className={`${styles.switch}`}>
                    <input
                      type="checkbox"
                      name="vital_sms_alert"
                      className={`${styles.input} ${styles.checkbox}`}
                      checked={formData.vital_sms_alert}
                      onChange={handleCheckBoxChange}
                    />
                    <span className={styles.slider}></span>
                  </label>
                  <span>Vital SMS Alert</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Alert */}
      <AlertDialogue
        open={deleteAlertOpen}
        onOpenChange={setDeleteAlertOpen}
        title="Delete Contact"
        description="Are you sure you want to delete this contact?"
      >
        <Button variant="normal" onClick={() => setDeleteAlertOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={() => deleteContactMutation(contactToDelete)}
          loading={isDeletingContact}
        >
          Delete
        </Button>
      </AlertDialogue>
    </div>
  );
};

export default PatientInfo;